/* Formatting and the vocabulary of the tool, in one place.

   Money is formatted Indian-style — 1,00,000 rather than 100,000 — because
   that is how the number will be read, and a lakh written the wrong way is a
   number people have to stop and count the digits of. */

import type { Interval, StrategyKind } from "../api/types";

export function rupees(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return (
    "₹" +
    value.toLocaleString("en-IN", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );
}

/** Gains and losses always carry their sign — an unsigned P&L is ambiguous. */
export function signedRupees(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return (value >= 0 ? "+" : "−") + rupees(Math.abs(value), decimals);
}

export function pct(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(decimals)}%`;
}

/** A gain is green, a loss is red, and exactly zero is neither. */
export function toneOf(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return "flat";
  return value > 0 ? "up" : "down";
}

export function clockTime(iso: string | null): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return at.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

export function clockFromMs(ms: number): string {
  return new Date(ms).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* Ordered by how much can go wrong, gentlest first — the dropdown reads in
   this order, and the first item is the one somebody who does not know which
   to pick should pick. */
export const KIND_LABEL: Record<StrategyKind, string> = {
  supertrend: "Supertrend",
  ema_cross: "EMA crossover",
  breakout: "Channel breakout",
  rsi_reversion: "RSI reversion",
  webhook: "Signals from elsewhere",
};

/*
 * An honest word about each rule's risk.
 *
 * Not marketing. The two that look most attractive on a backtest — breakout
 * and RSI reversion — are the two that are hardest to hold on to with real
 * money, and for opposite reasons, so both say so here rather than in a
 * footnote nobody reads.
 */
export const KIND_RISK: Record<
  StrategyKind,
  { label: string; tone: "muted" | "warn" | "down"; note: string }
> = {
  supertrend: {
    label: "steadiest",
    tone: "muted",
    note: "Trades least of the four, which on a small account matters more than the signal quality — every trade you do not take is a cost you do not pay. Suffers in a market that goes sideways for weeks.",
  },
  ema_cross: {
    label: "steady",
    tone: "muted",
    note: "The most understood rule there is. Follows a trend well and whipsaws in a flat market; on daily candles it trades rarely enough that the whipsaws are affordable.",
  },
  breakout: {
    label: "streaky",
    tone: "warn",
    note: "Most of its trades lose a little and a few win a lot, so it can be down for months and still be working. That is fine arithmetically and very hard to sit through — do not switch it off in the middle unless you have decided it is broken.",
  },
  rsi_reversion: {
    label: "deceptive",
    tone: "down",
    note: "Wins often and loses badly. Buying after a fall is buying from people who know something you do not, and the occasional trade that keeps falling gives back many small wins. Never run this one without a hard stop.",
  },
  webhook: {
    label: "depends",
    tone: "warn",
    note: "Has no opinion of its own. Its risk is whatever is posting to it — every limit here still applies, but nothing here can judge the signal.",
  },
};

/* What each rule actually does, in a sentence, because "RSI reversion" tells
   somebody choosing between them nothing at all. */
export const KIND_BLURB: Record<StrategyKind, string> = {
  ema_cross:
    "Buys when a short average of the price crosses above a longer one, and sells when it crosses back. Follows a trend; whipsaws in a flat market.",
  rsi_reversion:
    "Buys when the price has fallen hard and then turns back up, and sells once it has recovered. Waits for the turn, not merely the fall.",
  breakout:
    "Buys a close above the highest price of the last N bars, and sells a close below the lowest of the last few. Catches moves early and is stopped out often.",
  supertrend:
    "Follows a band set from recent volatility, buying when the trend flips up and selling when it flips down.",
  webhook:
    "Places no signals of its own. Something else — a TradingView alert, say — posts them to a URL, and every limit here still applies.",
};

/* The settings each rule takes, with the range the form allows. The backend
   validates the same numbers; this is here so a mistake is caught before the
   round trip rather than after. */
export interface ParamField {
  key: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  hint?: string;
}

export const PARAM_FIELDS: Record<StrategyKind, ParamField[]> = {
  ema_cross: [
    { key: "fast", label: "Fast EMA", min: 2, max: 200, hint: "Bars in the short average." },
    { key: "slow", label: "Slow EMA", min: 3, max: 400, hint: "Must be longer than the fast one." },
  ],
  rsi_reversion: [
    { key: "period", label: "RSI period", min: 2, max: 100 },
    { key: "oversold", label: "Buy above", min: 1, max: 99, hint: "RSI turning up through this." },
    { key: "overbought", label: "Sell at", min: 2, max: 100 },
  ],
  breakout: [
    { key: "entry_period", label: "Breakout bars", min: 2, max: 400 },
    { key: "exit_period", label: "Breakdown bars", min: 2, max: 400 },
  ],
  supertrend: [
    { key: "period", label: "ATR period", min: 2, max: 100 },
    { key: "multiplier", label: "Multiplier", min: 0.5, max: 10, step: 0.5 },
  ],
  webhook: [],
};

export const DEFAULT_PARAMS: Record<StrategyKind, Record<string, number>> = {
  ema_cross: { fast: 9, slow: 21 },
  rsi_reversion: { period: 14, oversold: 30, overbought: 65 },
  breakout: { entry_period: 20, exit_period: 10 },
  supertrend: { period: 10, multiplier: 3 },
  webhook: {},
};

export const INTERVALS: { value: Interval; label: string }[] = [
  { value: "minute", label: "1 minute" },
  { value: "3minute", label: "3 minutes" },
  { value: "5minute", label: "5 minutes" },
  { value: "10minute", label: "10 minutes" },
  { value: "15minute", label: "15 minutes" },
  { value: "30minute", label: "30 minutes" },
  { value: "60minute", label: "1 hour" },
  { value: "day", label: "1 day" },
];

export const ORDER_CAPTION: Record<string, string> = {
  PAPER: "On paper",
  PENDING: "Sending",
  OPEN: "With Zerodha",
  COMPLETE: "Filled",
  CANCELLED: "Cancelled",
  REJECTED: "Rejected by Zerodha",
  REJECTED_LOCAL: "Not sent",
};

export const EXIT_CAPTION: Record<string, string> = {
  signal: "the rule said so",
  "stop-loss": "stop-loss",
  target: "target",
  "trailing-stop": "trailing stop",
  "square-off": "squared off",
  manual: "closed by hand",
  panic: "stopped by hand",
};

/** Zerodha's request token comes back in the URL after a successful login. */
export function requestTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get("status") && params.get("status") !== "success") return null;
  return params.get("request_token");
}

/** A compact figure for a tile — 1.2L, 3.4Cr — where the exact rupee is noise. */
export function compactRupees(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value < 0 ? "−" : "";
  const n = Math.abs(value);
  if (n >= 1e7) return `${sign}₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${sign}₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `${sign}₹${(n / 1e3).toFixed(1)}k`;
  return `${sign}₹${n.toFixed(0)}`;
}

/** Traded volume, which is counted in shares and reads better abbreviated. */
export function compactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const n = Math.abs(value);
  if (n >= 1e7) return `${(value / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `${(value / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  return String(value);
}

/** A price. Always two decimals — a stock at 1,432.5 reads as unfinished. */
export function price(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** "4m ago" — for the age of a feed, where the exact second is not the point. */
export function ago(ms: number): string {
  if (ms < 0) return "never";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

export const HALF_HOUR_MS = 30 * 60 * 1000;

/** How many days of history a chart wants at each interval. */
export const DAYS_FOR: Record<Interval, number> = {
  minute: 3,
  "3minute": 8,
  "5minute": 12,
  "10minute": 20,
  "15minute": 30,
  "30minute": 60,
  "60minute": 120,
  day: 365,
};

/*
 * What Zerodha takes out of a round trip, in rupees.
 *
 * This is here because it is the single most important number a small account
 * has to see and almost never does. At ₹3,300 a trade, delivery charges are
 * about ₹23 — 0.7% — so a rule has to be right by more than 0.7% just to
 * break even, before it has made a paisa. Three trades a day on ₹10,000 of
 * capital is roughly 2% of the account gone in costs alone, daily.
 *
 * An estimate, and labelled as one everywhere it is shown: the exact figures
 * are on the contract note. But it is the right order of magnitude, and it
 * matches what the server uses when it reports estimated charges, so the two
 * never disagree.
 *
 * Rates as of the 2026 schedule:
 *
 *   CNC (delivery)   no brokerage; STT 0.1% each way; stamp 0.015% on buy;
 *                    exchange txn ~0.00297% each way; SEBI 0.0001%; GST 18%
 *                    on the charges (not on STT or stamp); and a flat DP
 *                    charge on every sell, which is what hurts a small trade.
 *   MIS (intraday)   brokerage min(₹20, 0.03%) per side; STT 0.025% on sell
 *                    only; stamp 0.003% on buy; no DP charge.
 */

/** Zerodha's flat depository charge per scrip on a delivery sell. */
const DP_CHARGE = 15.93;

export interface TradeCost {
  rupees: number;
  /** As a percentage of the notional — what the rule has to beat. */
  pct: number;
}

export function roundTripCost(notional: number, product: "CNC" | "MIS"): TradeCost {
  if (!notional || notional <= 0) return { rupees: 0, pct: 0 };

  let cost: number;
  if (product === "CNC") {
    const stt = notional * 0.001 * 2;
    const stamp = notional * 0.00015;
    const txn = notional * 0.0000297 * 2;
    const sebi = notional * 0.000001 * 2;
    const gst = (txn + sebi) * 0.18;
    cost = stt + stamp + txn + sebi + gst + DP_CHARGE;
  } else {
    const brokerage = Math.min(20, notional * 0.0003) * 2;
    const stt = notional * 0.00025;
    const stamp = notional * 0.00003;
    const txn = notional * 0.0000297 * 2;
    const sebi = notional * 0.000001 * 2;
    const gst = (brokerage + txn + sebi) * 0.18;
    cost = brokerage + stt + stamp + txn + sebi + gst;
  }
  return { rupees: cost, pct: (cost / notional) * 100 };
}
